// パネルのドラッグ移動（ポップアウト）。ヘッダを掴んで画面内の好きな位置へ動かせる。
//
// 位置が null の間は「初期位置（右下）」の意味で、CSS の fixed bottom/right に任せる。
// 一度ドラッグしたら fixed left/top の座標管理に切り替わる。
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, RefObject } from 'react';

export interface UseDragResult {
  /**
   * ドラッグ対象（パネルの最外殻）に付ける ref。
   * 型引数に | null を入れてはいけない。JSX の <div ref=…> が要求するのは
   * RefObject<HTMLDivElement> で、| null 付きは代入できず tsc -b（Cloudflare のビルド）が落ちる。
   */
  ref: RefObject<HTMLDivElement>;
  /** 現在位置。null は「まだ動かしていない」の意味。 */
  pos: { x: number; y: number } | null;
  /** パネルに適用するスタイル。未ドラッグなら空オブジェクト（初期位置のまま）。 */
  style: CSSProperties;
  /** 指定座標へ移動する（ポップアウト直後の初期配置用）。 */
  moveTo: (x: number, y: number) => void;
  /** 掴む場所（ヘッダ）の onPointerDown に付けるハンドラ。 */
  onPointerDown: (e: ReactPointerEvent) => void;
}

export function useDrag(): UseDragResult {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // ドラッグ中にアンマウントされた場合も window のリスナーを確実に外す
  const detachRef = useRef<() => void>(() => {});
  useEffect(() => () => detachRef.current(), []);

  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    const el = ref.current;
    if (!el) return;
    e.preventDefault(); // ドラッグ中のテキスト選択を防ぐ
    const rect = el.getBoundingClientRect();
    const grabX = e.clientX - rect.left;
    const grabY = e.clientY - rect.top;

    const move = (ev: globalThis.PointerEvent) => {
      // 画面外へ出て掴めなくならないよう、可動範囲を画面内に制限する
      const maxX = Math.max(window.innerWidth - rect.width, 0);
      const maxY = Math.max(window.innerHeight - 40, 0); // ヘッダ分は必ず画面内に残す
      setPos({
        x: Math.min(Math.max(ev.clientX - grabX, 0), maxX),
        y: Math.min(Math.max(ev.clientY - grabY, 0), maxY),
      });
    };
    const detach = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', detach);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', detach);
    detachRef.current = detach;
  }, []);

  const moveTo = useCallback((x: number, y: number) => setPos({ x, y }), []);

  return {
    ref,
    pos,
    style: pos ? { position: 'fixed', left: pos.x, top: pos.y } : {},
    moveTo,
    onPointerDown,
  };
}
